import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import testImg from '@/assets/img/video_sample_img.png'
import aalpacaImg from '@/assets/img/alpaca.png'
import aalpacaSmileImg from '@/assets/img/alpaca_smlie.png'
import { motion, AnimatePresence } from 'framer-motion'
import AnswerCircle from '@/components/interAction/AnswerCircle'
import AnswerCheck from '@/components/interAction/AnswerCheck'
import Toast from '@/components/interAction/Toast'

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let youTubeApiPromise: Promise<void> | null = null;

const loadYouTubeIframeAPI = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window is undefined'));
  }

  if (window.YT && window.YT.Player) {
    return Promise.resolve();
  }

  if (youTubeApiPromise) {
    return youTubeApiPromise;
  }

  youTubeApiPromise = new Promise<void>((resolve, reject) => {
    const scriptId = 'youtube-iframe-api';
    const existingScript = document.getElementById(scriptId);

    if (!existingScript) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.id = scriptId;
      tag.async = true;
      tag.onerror = (error) => {
        console.error('YouTube API 로드 실패:', error);
        youTubeApiPromise = null;
        reject(error instanceof Error ? error : new Error(String(error)));
      };
      document.body.appendChild(tag);
    }

    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      resolve();
    };
  });

  return youTubeApiPromise;
};

const isYouTubeUrl = (url: string) => /youtu\.be|youtube\.com/i.test(url || '');

const extractYouTubeVideoId = (url: string) => {
  if (!url) return null;
  const shortMatch = url.match(/youtu\.be\/([^?]+)/);
  if (shortMatch?.[1]) {
    return shortMatch[1];
  }
  const longMatch = url.match(/[?&]v=([^&]+)/);
  if (longMatch?.[1]) {
    return longMatch[1];
  }
  const embedMatch = url.match(/embed\/([^?]+)/);
  if (embedMatch?.[1]) {
    return embedMatch[1];
  }
  return null;
};

type RawAnswerValue = number | number[] | string | null | undefined;

const normalizeNumericArray = (value: RawAnswerValue): number[] => {
  const bucket: number[] = [];
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      const numeric = typeof entry === 'number' ? entry : Number(entry);
      if (Number.isFinite(numeric)) {
        bucket.push(Math.trunc(numeric));
      }
    });
  } else if (typeof value === 'number') {
    if (Number.isFinite(value)) {
      bucket.push(Math.trunc(value));
    }
  } else if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      bucket.push(Math.trunc(parsed));
    }
  }
  const unique = Array.from(new Set(bucket));
  unique.sort((a, b) => a - b);
  return unique;
};

type AnswerMap = Record<number, number[]>;

type RetryFeedback = {
  type: 'success' | 'error'
  message: string
}

const normalizeAnswerMap = (answers?: Record<string | number, RawAnswerValue>): AnswerMap => {
  if (!answers) {
    return {};
  }
  const normalized: AnswerMap = {};
  Object.entries(answers).forEach(([key, value]) => {
    const numericKey = Number(key);
    if (Number.isNaN(numericKey)) {
      return;
    }
    const normalizedValues = normalizeNumericArray(value);
    if (normalizedValues.length > 0) {
      normalized[numericKey] = normalizedValues;
    }
  });
  return normalized;
};

const normalizeRetryAnswers = (answers?: Record<string | number, RawAnswerValue>): AnswerMap =>
  normalizeAnswerMap(answers);

const convertConfigAnswersToOneBased = (value: RawAnswerValue): number[] =>
  normalizeNumericArray(value).map((idx) => idx + 1);

const normalizeSelection = (selection: number[]): number[] => {
  const unique = Array.from(new Set(selection));
  unique.sort((a, b) => a - b);
  return unique;
};

const toggleSelectionValue = (current: number[], optionId: number): number[] => {
  if (current.includes(optionId)) {
    return current.filter((val) => val !== optionId);
  }
  return normalizeSelection([...current, optionId]);
};

const hasSelections = (selection?: number[]): boolean =>
  Array.isArray(selection) && selection.length > 0;

const areSelectionsEqual = (selection: number[] | undefined, correct: number[]): boolean => {
  if (!hasSelections(selection) || correct.length === 0) {
    return false;
  }
  const normalizedSelection = normalizeSelection(selection!);
  const normalizedCorrect = normalizeSelection(correct);
  if (normalizedSelection.length !== normalizedCorrect.length) {
    return false;
  }
  return normalizedSelection.every((value, idx) => value === normalizedCorrect[idx]);
};

interface Option {
  id: number
  label: string
}
interface Question {
  id: number
  text: string
  options: Option[]
  correctOptionId: number | null
  correctOptionIds: number[]
  allowMultiple: boolean
  reason?: string
}

interface VideoProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
}

export default function LeaningVideo({ activity, unitData, onResponse, existingResponse, isHomework = false }: VideoProps) {
  // activity.items에서 실제 데이터 가져오기
  const activityData = activity?.items?.[0] || {};
  let videoUrl = '';
  let quizzes: any[] = [];
  
  try {
    // contentJson이나 configJson에서 데이터 파싱
    const jsonData = activityData.contentJson || activity?.configJson || '{}';
    const parsedData = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
    
    videoUrl = parsedData.videoUrl || '';
    quizzes = parsedData.quizzes || [];
    
    console.log('=== 동영상 퀴즈 데이터 ===');
    console.log('videoUrl:', videoUrl);
    console.log('quizzes:', quizzes);
  } catch (e) {
    console.error('동영상 퀴즈 데이터 파싱 에러:', e);
  }

// quizzes 데이터를 Question 형식으로 변환
const questions: Question[] = quizzes.map((quiz, index) => {
  const correctOptionIds = convertConfigAnswersToOneBased(quiz.answer);
  const allowMultiple = quiz.allowMultiple === true || correctOptionIds.length > 1;
  return {
    id: index + 1,
    text: quiz.question || '',
    options: (quiz.choices || []).map((choice: string, idx: number) => ({
      id: idx + 1, // 1부터 시작
      label: choice
    })),
    correctOptionId: correctOptionIds.length > 0 ? correctOptionIds[0] : null,
    correctOptionIds,
    allowMultiple,
    reason: quiz.reason || ''
  };
});

  const gatingEnabled = true;

  // existingResponse에서 초기 답변 복원 (재입장 대비)
  const initialAnswersRef = useRef<AnswerMap>(normalizeAnswerMap(existingResponse?.selectedAnswers));
  type QuestionStatus = 'UNANSWERED' | 'CORRECT' | 'INCORRECT';
  const hasInitialAnswers = useRef(Object.keys(initialAnswersRef.current).length > 0).current;
  const initialAnswersSnapshot = initialAnswersRef.current;

  const computeHomeworkReady = (answers?: AnswerMap, retryAnswers?: AnswerMap) => {
    const source = answers || selectedAnswers;
    if (questions.length === 0) {
      return true;
    }

    const allAnswered = questions.every((q) => hasSelections(source?.[q.id]));
    if (!allAnswered) {
      return false;
    }

    const incorrectIds = questions
      .filter((q) => !areSelectionsEqual(source?.[q.id], q.correctOptionIds))
      .map((q) => q.id);

    if (incorrectIds.length === 0) {
      return true;
    }

    const retrySource = retryAnswers || retrySelections;
    return incorrectIds.every((id) => hasSelections(retrySource?.[id]));
  };
  
  const [selectedAnswers, setSelectedAnswers] = useState<AnswerMap>(initialAnswersRef.current);
  const [multiSelections, setMultiSelections] = useState<Record<number, number[]>>({});
  const [toasts, setToasts] = useState<Record<number, number | null>>({})
  const [visibleCount, setVisibleCount] = useState(0);
  const [retrySelections, setRetrySelections] = useState<AnswerMap>(
    () => normalizeRetryAnswers(existingResponse?.retryAnswers)
  );
  const [retryMultiSelections, setRetryMultiSelections] = useState<Record<number, number[]>>({});
  const [retryFeedbacks, setRetryFeedbacks] = useState<Record<number, RetryFeedback | undefined>>({});
  const [retryComplete, setRetryComplete] = useState(
    () => existingResponse?.isCorrect === true && existingResponse?.isHomeworkReady === true
  );
  const toastTimers = useRef<Record<number, NodeJS.Timeout>>({})
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const youTubePlayerRef = useRef<any>(null);
  const youTubeProgressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const youTubeContainerIdRef = useRef<string>('');
  const hasSavedRetryAnswers = Object.keys(existingResponse?.retryAnswers || {}).length > 0;
  const initialCompletionStage: 'none' | 'initial' | 'retry' = (() => {
    if (!existingResponse?.isCorrect) {
      return 'none';
    }
    if (existingResponse?.isHomeworkReady === true) {
      return hasSavedRetryAnswers ? 'retry' : 'initial';
    }
    return 'none';
  })();
  const completionStageRef = useRef<'none' | 'initial' | 'retry'>(initialCompletionStage);
  const initialResponseSyncedRef = useRef(false);
  if (!youTubeContainerIdRef.current) {
    youTubeContainerIdRef.current = `youtube-player-${Math.random().toString(36).slice(2, 11)}`;
  }

  const [videoProgress, setVideoProgress] = useState(0);
  const [quizUnlocked, setQuizUnlocked] = useState(
    () => !gatingEnabled || hasInitialAnswers || existingResponse?.isCorrect === true
  );
  const progressPercent = Math.min(100, Math.round(videoProgress * 100));
  const isYouTube = useMemo(() => isYouTubeUrl(videoUrl), [videoUrl]);
  const youTubeVideoId = useMemo(() => (isYouTube ? extractYouTubeVideoId(videoUrl) : null), [isYouTube, videoUrl]);
  const isVideoOnly = questions.length === 0;
  const videoOnlyReadyRef = useRef<boolean | null>(null);

  // 초기 로드 시 isCorrect 계산 (existingResponse가 있을 때)
  useEffect(() => {
    if (!onResponse || initialResponseSyncedRef.current) {
      return;
    }

    if (isVideoOnly) {
      return;
    }

    if (existingResponse?.isCorrect) {
      onResponse({
        ...existingResponse,
        isHomeworkReady: computeHomeworkReady(initialAnswersSnapshot, retrySelections)
      });
      initialResponseSyncedRef.current = true;
      return;
    }

    if (hasInitialAnswers) {
      const allAnswered = questions.every((q) => hasSelections(initialAnswersSnapshot[q.id]));
      const allCorrect =
        allAnswered &&
        questions.every((q) => areSelectionsEqual(initialAnswersSnapshot[q.id], q.correctOptionIds));
      
      onResponse({
        selectedAnswers: initialAnswersSnapshot,
        isCorrect: allCorrect,
        isHomeworkReady: computeHomeworkReady(initialAnswersSnapshot, retrySelections),
      });
      initialResponseSyncedRef.current = true;
    } else if (!existingResponse) {
      onResponse({
        selectedAnswers: {},
        isCorrect: false,
        isHomeworkReady: false,
      });
      initialResponseSyncedRef.current = true;
    }
  }, [existingResponse, hasInitialAnswers, initialAnswersSnapshot, isVideoOnly, onResponse, questions, retrySelections]);

  useEffect(() => {
    if (!onResponse || !isVideoOnly) {
      return;
    }

    if (existingResponse?.isCorrect === true) {
      if (videoOnlyReadyRef.current !== true) {
        onResponse({
          ...existingResponse,
          selectedAnswers: existingResponse?.selectedAnswers || {},
          isCorrect: true,
          isHomeworkReady: true,
        });
        videoOnlyReadyRef.current = true;
      }
      return;
    }

    const meetsThreshold = videoProgress >= 0.75;
    if (videoOnlyReadyRef.current === meetsThreshold) {
      return;
    }
    videoOnlyReadyRef.current = meetsThreshold;
    onResponse({
      selectedAnswers: existingResponse?.selectedAnswers || {},
      isCorrect: meetsThreshold,
      isHomeworkReady: meetsThreshold,
    });
  }, [existingResponse, isVideoOnly, onResponse, videoProgress]);
  useEffect(() => {
    if (questions.length === 0) {
      setQuizUnlocked(true);
      return;
    }
    if (!gatingEnabled) {
      setQuizUnlocked(true);
      return;
    }
    if (hasInitialAnswers || existingResponse?.isCorrect) {
      setQuizUnlocked(true);
    } else {
      setQuizUnlocked(false);
    }
  }, [existingResponse?.isCorrect, gatingEnabled, hasInitialAnswers, questions.length]);

  const questionStatuses = useMemo(() => {
    const statusMap: Record<number, QuestionStatus> = {};
    questions.forEach((q) => {
      const selection = selectedAnswers[q.id];
      if (!hasSelections(selection)) {
        statusMap[q.id] = 'UNANSWERED';
      } else if (areSelectionsEqual(selection, q.correctOptionIds)) {
        statusMap[q.id] = 'CORRECT';
      } else {
        statusMap[q.id] = 'INCORRECT';
      }
    });
    return statusMap;
  }, [questions, selectedAnswers]);

  const evaluateAnswers = useCallback((answers: AnswerMap) => {
    const allAnswered = questions.every((q) => hasSelections(answers[q.id]));
    const allCorrect =
      allAnswered &&
      questions.every((q) => areSelectionsEqual(answers[q.id], q.correctOptionIds));
    return { allAnswered, allCorrect };
  }, [questions]);

  const getCorrectAnswerMeta = (question: Question) =>
    question.correctOptionIds
      .map((id) => {
        const option = question.options.find((opt) => opt.id === id);
        if (!option) {
          return null;
        }
        return { id, label: option.label };
      })
      .filter((entry): entry is { id: number; label: string } => !!entry);

  const initialPassCompleted = useMemo(() => {
    if (questions.length === 0) {
      return false;
    }
    return questions.every((q) => questionStatuses[q.id] !== 'UNANSWERED');
  }, [questions, questionStatuses]);

  const nextVisibleFromAnswers = useCallback(() => {
    if (!quizUnlocked || questions.length === 0) {
      return 0;
    }
    if (hasInitialAnswers) {
      return questions.length;
    }
    const answeredCount = questions.filter((q) => hasSelections(selectedAnswers[q.id])).length;
    return Math.min(questions.length, Math.max(1, answeredCount + 1));
  }, [quizUnlocked, questions, selectedAnswers, hasInitialAnswers]);

  useEffect(() => {
    const nextVisible = nextVisibleFromAnswers();
    if (nextVisible !== visibleCount) {
      setVisibleCount(nextVisible);
    }
  }, [nextVisibleFromAnswers, visibleCount]);

  useEffect(() => {
    if (!gatingEnabled) return;
    if (videoProgress >= 0.75 && !quizUnlocked) {
      setQuizUnlocked(true);
    }
  }, [gatingEnabled, quizUnlocked, videoProgress]);

  const incorrectQuestions = useMemo(
    () => questions.filter((q) => questionStatuses[q.id] === 'INCORRECT'),
    [questions, questionStatuses]
  );

  useEffect(() => {
    if (!initialPassCompleted) {
      if (Object.keys(retrySelections).length > 0) {
        setRetrySelections({});
      }
      if (Object.keys(retryMultiSelections).length > 0) {
        setRetryMultiSelections({});
      }
      if (Object.keys(retryFeedbacks).length > 0) {
        setRetryFeedbacks({});
      }
      setRetryComplete(false);
      completionStageRef.current = 'none';
      return;
    }

    const filteredSelections: AnswerMap = {};
    incorrectQuestions.forEach((q) => {
      const retrySelection = retrySelections[q.id];
      if (hasSelections(retrySelection)) {
        filteredSelections[q.id] = retrySelection!;
      }
    });
    if (Object.keys(filteredSelections).length !== Object.keys(retrySelections).length) {
      setRetrySelections(filteredSelections);
    }

    const filteredFeedbacks: Record<number, RetryFeedback | undefined> = {};
    incorrectQuestions.forEach((q) => {
      if (retryFeedbacks[q.id]) {
        filteredFeedbacks[q.id] = retryFeedbacks[q.id];
      }
    });
    if (Object.keys(filteredFeedbacks).length !== Object.keys(retryFeedbacks).length) {
      setRetryFeedbacks(filteredFeedbacks);
    }

    if (incorrectQuestions.length === 0) {
      setRetryComplete(true);
      if (completionStageRef.current !== 'initial') {
        completionStageRef.current = 'initial';
        onResponse?.({
          selectedAnswers,
          isCorrect: true,
          isHomeworkReady: computeHomeworkReady(selectedAnswers, filteredSelections),
        });
      }
      return;
    }

    const allRetried = incorrectQuestions.every((q) => hasSelections(filteredSelections[q.id]));
    setRetryComplete(allRetried);

    if (allRetried && completionStageRef.current !== 'retry') {
      completionStageRef.current = 'retry';
      onResponse?.({
        selectedAnswers,
        retryAnswers: filteredSelections,
        isCorrect: true,
        isHomeworkReady: computeHomeworkReady(selectedAnswers, filteredSelections),
      });
    }
  }, [initialPassCompleted, incorrectQuestions, onResponse, retryFeedbacks, retryMultiSelections, retrySelections, selectedAnswers]);

  const stopYouTubeProgressMonitor = useCallback(() => {
    if (youTubeProgressTimerRef.current) {
      clearInterval(youTubeProgressTimerRef.current);
      youTubeProgressTimerRef.current = null;
    }
  }, []);

  const startYouTubeProgressMonitor = useCallback(() => {
    stopYouTubeProgressMonitor();
    youTubeProgressTimerRef.current = setInterval(() => {
      const player = youTubePlayerRef.current;
      if (!player?.getDuration || !player?.getCurrentTime) {
        return;
      }
      const duration = player.getDuration();
      const current = player.getCurrentTime();
      if (duration > 0) {
        setVideoProgress(current / duration);
      }
    }, 500);
  }, [stopYouTubeProgressMonitor]);

  useEffect(() => {
    if (!isYouTube || !youTubeVideoId || typeof window === 'undefined') {
      return;
    }
    let isMounted = true;
    loadYouTubeIframeAPI()
      .then(() => {
        if (!isMounted) return;
        youTubePlayerRef.current = new window.YT.Player(youTubeContainerIdRef.current, {
          videoId: youTubeVideoId,
          playerVars: {
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
          },
          events: {
            onStateChange: (event: any) => {
              if (!window.YT || !window.YT.PlayerState) return;
              const playerState = window.YT.PlayerState;
              if (event.data === playerState.PLAYING) {
                startYouTubeProgressMonitor();
              } else if (
                event.data === playerState.PAUSED ||
                event.data === playerState.BUFFERING ||
                event.data === playerState.CUED
              ) {
                stopYouTubeProgressMonitor();
              } else if (event.data === playerState.ENDED) {
                stopYouTubeProgressMonitor();
                setVideoProgress(1);
              }
            },
            onReady: () => {
              const duration = youTubePlayerRef.current?.getDuration?.();
              if (duration) {
                const current = youTubePlayerRef.current?.getCurrentTime?.() || 0;
                setVideoProgress(current / duration);
              }
            },
          },
        });
      })
      .catch((error) => {
        console.error('YouTube 플레이어 초기화 실패:', error);
      });

  return () => {
      isMounted = false;
      stopYouTubeProgressMonitor();
      if (youTubePlayerRef.current) {
        youTubePlayerRef.current.destroy();
        youTubePlayerRef.current = null;
      }
    };
  }, [isYouTube, startYouTubeProgressMonitor, stopYouTubeProgressMonitor, youTubeVideoId]);

  const handleVideoTimeUpdate = () => {
    const videoEl = videoElementRef.current;
    if (!videoEl || !videoEl.duration) return;
    setVideoProgress(videoEl.currentTime / videoEl.duration);
  };

  const handleVideoLoadedMetadata = () => {
    handleVideoTimeUpdate();
  };

  const handleVideoEnded = () => {
    setVideoProgress(1);
  };

  const setFeedbackForQuestion = useCallback((questionId: number, feedback?: RetryFeedback) => {
    setRetryFeedbacks((prev) => {
      if (!feedback) {
        if (!prev[questionId]) {
          return prev;
        }
        const next = { ...prev };
        delete next[questionId];
        return next;
      }
      return {
        ...prev,
        [questionId]: feedback,
      };
    });
  }, []);

  const handleSelect = (qId: number, optId: number) => {
    if (gatingEnabled && !quizUnlocked) {
      return;
    }
    const question = questions.find(q => q.id === qId);
    if (!question) {
      return;
    }
    if (questionStatuses[qId] && questionStatuses[qId] !== 'UNANSWERED') {
      return;
    }

    if (question.allowMultiple) {
      setMultiSelections((prev) => {
        const current = prev[qId] || [];
        const nextSelection = toggleSelectionValue(current, optId);
        if (nextSelection.length === 0) {
          const { [qId]: _, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [qId]: nextSelection,
        };
      });
      return;
    }

    const normalizedSelection = normalizeSelection([optId]);
    const updatedAnswers: AnswerMap = {
      ...selectedAnswers,
      [qId]: normalizedSelection,
    };
    setSelectedAnswers(updatedAnswers)

    const { allAnswered, allCorrect } = evaluateAnswers(updatedAnswers);

    // 답변 저장 (isCorrect 포함)
    if (onResponse) {
      onResponse({ 
        selectedAnswers: updatedAnswers,
        isCorrect: allCorrect,
        isHomeworkReady: computeHomeworkReady(updatedAnswers)
      });
    }

    // 무조건 새 토스트 상태 부여 (timestamp)
    const newToastId = Date.now()
    setToasts(prev => ({
      ...prev,
      [qId]: newToastId,
    }))

    // 기존 타이머 제거
    if (toastTimers.current[qId]) {
      clearTimeout(toastTimers.current[qId])
    }

    // 2초 후 자동 삭제
    toastTimers.current[qId] = setTimeout(() => {
      setToasts(prev => ({
        ...prev,
        [qId]: null,
      }))
    }, 2000)
  };

  const handleMultiSubmit = (question: Question) => {
    if (questionStatuses[question.id] && questionStatuses[question.id] !== 'UNANSWERED') {
      return;
    }
    const pendingSelection = multiSelections[question.id];
    if (!hasSelections(pendingSelection)) {
      return;
    }

    const normalizedSelection = normalizeSelection(pendingSelection!);
    const updatedAnswers: AnswerMap = {
      ...selectedAnswers,
      [question.id]: normalizedSelection,
    };
    setSelectedAnswers(updatedAnswers);
    setMultiSelections((prev) => {
      const { [question.id]: _, ...rest } = prev;
      return rest;
    });

    const { allAnswered, allCorrect } = evaluateAnswers(updatedAnswers);

    if (onResponse) {
      onResponse({
        selectedAnswers: updatedAnswers,
        isCorrect: allCorrect,
        isHomeworkReady: computeHomeworkReady(updatedAnswers),
      });
    }

    const toastId = Date.now();
    setToasts((prev) => ({
      ...prev,
      [question.id]: toastId,
    }));

    if (toastTimers.current[question.id]) {
      clearTimeout(toastTimers.current[question.id]);
    }

    toastTimers.current[question.id] = setTimeout(() => {
      setToasts((prev) => ({
        ...prev,
        [question.id]: null,
      }));
    }, 2000);
  };

  const handleRetrySelect = (question: Question, optId: number) => {
    if (hasSelections(retrySelections[question.id])) {
      return;
    }
    setFeedbackForQuestion(question.id);
    if (question.allowMultiple) {
      setRetryMultiSelections((prev) => {
        const current = prev[question.id] || [];
        const nextSelection = toggleSelectionValue(current, optId);
        if (nextSelection.length === 0) {
          const { [question.id]: _, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [question.id]: nextSelection,
        };
      });
      return;
    }
    const normalizedSelection = normalizeSelection([optId]);
    if (question.correctOptionIds.length === 0) {
      setRetrySelections((prev) => ({
        ...prev,
        [question.id]: normalizedSelection,
      }));
      return;
    }
    if (!areSelectionsEqual(normalizedSelection, question.correctOptionIds)) {
      setFeedbackForQuestion(question.id, {
        type: 'error',
        message: '틀렸습니다. 다시 선택해주세요.',
      });
      return;
    }
    setRetrySelections((prev) => ({
      ...prev,
      [question.id]: normalizedSelection,
    }));
    setFeedbackForQuestion(question.id, {
      type: 'success',
      message: '정답입니다! 🎉',
    });
  };

  const handleRetrySubmit = (question: Question) => {
    if (hasSelections(retrySelections[question.id])) {
      return;
    }
    const pendingSelection = retryMultiSelections[question.id];
    if (!hasSelections(pendingSelection)) {
      return;
    }
    const normalized = normalizeSelection(pendingSelection!);
    if (question.correctOptionIds.length === 0) {
      setRetrySelections((prev) => ({
        ...prev,
        [question.id]: normalized,
      }));
      setRetryMultiSelections((prev) => {
        const { [question.id]: _, ...rest } = prev;
        return rest;
      });
      setFeedbackForQuestion(question.id);
      return;
    }
    if (!areSelectionsEqual(normalized, question.correctOptionIds)) {
      setRetryMultiSelections((prev) => {
        if (!prev[question.id]) {
          return prev;
        }
        const { [question.id]: _, ...rest } = prev;
        return rest;
      });
      setFeedbackForQuestion(question.id, {
        type: 'error',
        message: '틀렸습니다. 다시 선택해주세요.',
      });
      return;
    }
    setRetrySelections((prev) => ({
      ...prev,
      [question.id]: normalized,
    }));
    setRetryMultiSelections((prev) => {
      const { [question.id]: _, ...rest } = prev;
      return rest;
    });
    setFeedbackForQuestion(question.id, {
      type: 'success',
      message: '정답입니다! 🎉',
    });
  };

  const quizLocked = gatingEnabled && !quizUnlocked;
  const visibleQuestions = useMemo(() => {
    if (visibleCount <= 0) return [];
    const limit = Math.min(visibleCount, questions.length);
    return questions.slice(0, limit);
  }, [questions, visibleCount]);

  return (
    <main>
      <section className='p-5'>
        <h1 className='pb-3 heading1'>동영상을 듣고, 퀴즈를 풀어주세요</h1>
        {videoUrl ? (
          isYouTube && youTubeVideoId ? (
            <div className='overflow-hidden w-full bg-black rounded-2xl aspect-video'>
              <div id={youTubeContainerIdRef.current} className='w-full h-full' />
            </div>
          ) : (
            <video
              ref={videoElementRef}
              src={videoUrl}
              controls
              className='w-full bg-black rounded-2xl aspect-video'
              onTimeUpdate={handleVideoTimeUpdate}
              onLoadedMetadata={handleVideoLoadedMetadata}
              onEnded={handleVideoEnded}
            />
          )
        ) : (
          <img src={testImg} className='rounded-2xl aspect-video' alt='비디오 샘플' />
        )}
        {/* {gatingEnabled && (
          <p className='mt-3 text-sm text-gray-600'>
            영상 시청률: {progressPercent}%
          </p>
        )}
        {quizLocked && (
          <p className='mt-2 text-sm font-medium text-indigo-600'>
            영상을 75% 이상 시청하면 문제가 활성화됩니다.
          </p>
        )} */}
      </section>

      <div className='flex flex-col'>
        {questions.length === 0 && !videoUrl && (
          <section className='p-5'>
            <p className='text-center text-gray-500'>등록된 퀴즈가 없습니다.</p>
          </section>
        )}
        {quizLocked ? (
          <div className='px-5 py-8'>
            <div className='p-5 text-center text-gray-700 bg-white rounded-2xl border border-gray-300 border-dashed'>
              동영상을 75% 이상 시청하면 퀴즈가 공개됩니다.
            </div>
          </div>
        ) : (
          <>
            {visibleQuestions.length === 0 ? (
              <div className='px-5 py-8 text-center text-gray-500'>
                영상을 먼저 시청한 뒤 문제를 확인할 수 있습니다.
              </div>
            ) : (
              <>
                {/* <p className='px-5 mb-2 text-sm text-gray-500'>
                  문항 {visibleQuestions.length} / {questions.length}
                </p> */}
                {visibleQuestions.map((question) => {
                  const status = questionStatuses[question.id] || 'UNANSWERED';
                  const isAnswered = status !== 'UNANSWERED';
                  const isCorrect = status === 'CORRECT';
                  const toastKey = toasts[question.id];
                  const questionIndex = questions.findIndex((q) => q.id === question.id);
                  const finalSelection = selectedAnswers[question.id] || [];
                  const pendingSelection = !isAnswered && question.allowMultiple ? (multiSelections[question.id] || []) : [];
                  const activeSelection = isAnswered ? finalSelection : pendingSelection;
                  const correctAnswerMeta = getCorrectAnswerMeta(question);

          return (
                    <section key={`initial-${question.id}`} className='relative p-5'>
              <AnimatePresence mode="wait">
                {toastKey != null && (
                  <Toast
                            key={`toast-${toastKey}`}
                    type={isCorrect ? 'success' : 'error'}
                    onClose={() => {
                      setToasts(prev => ({
                        ...prev,
                        [question.id]: null,
                      }))
                    }}
                    className="flex absolute -top-12 right-1/2 z-20 justify-center items-center translate-x-1/2"
                  >
                    <div className="flex items-center h-[80px] gap-2 px-5 pt-2 transition-none">
                      <img
                        src={isCorrect ? aalpacaSmileImg : aalpacaImg}
                        className="max-h-full"
                        alt=""
                      />
                      <p className="font-bold text-white">
                        {isCorrect ? '정답입니다! 🎉' : '틀렸습니다 😢'}
                      </p>
                    </div>
                  </Toast>
                )}
              </AnimatePresence>

              <div className='relative'>
                <h2 className='text-lg font-bold'>
                          {(questionIndex + 1)}. {question.text}
                </h2>

                        {isAnswered && (
                  isCorrect ? (
                    <AnswerCircle active={true} className='absolute -left-4 -top-2.5' size={50} />
                  ) : (
                    <AnswerCheck active={true} className='absolute -left-1 -top-5' />
                  )
                )}
              </div>

              <ul className='flex flex-wrap gap-y-5 gap-x-10 mt-6 mb-4'>
                {question.options.map(opt => (
                  <li key={opt.id}>
                    <button
                              className={`flex relative gap-2 items-center p-1 rounded-md ${isAnswered ? 'opacity-60 cursor-not-allowed' : 'hover:bg-indigo-100'}`}
                      onClick={() => handleSelect(question.id, opt.id)}
                              disabled={isAnswered}
                    >
                      <div className='relative'>
                        <AnswerCircle
                          active={activeSelection.includes(opt.id)}
                          className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
                        />
                        <span className='number_btn'>{opt.id}</span>
                      </div>
                      <span className='font-bold'>{opt.label}</span>
                    </button>
                  </li>
                ))}
              </ul>

              {question.allowMultiple && !isAnswered && (
                <div className='flex flex-wrap gap-3 items-center mt-2 text-sm text-gray-600'>
                  <span>정답을 여러 개 선택할 수 있습니다. 선택을 완료한 뒤 버튼을 눌러주세요.</span>
                  <button
                    type="button"
                    className='px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-full disabled:opacity-40 disabled:cursor-not-allowed'
                    onClick={() => handleMultiSubmit(question)}
                    disabled={!hasSelections(pendingSelection)}
                  >
                    선택 완료
                  </button>
                  {pendingSelection.length > 0 && (
                    <span className='font-semibold text-indigo-600'>
                      현재 {pendingSelection.length}개 선택됨
                    </span>
                  )}
                </div>
              )}

              <AnimatePresence initial={false}>
                        {isAnswered && !isCorrect && (
                  <motion.div
                            key={`feedback-initial-${question.id}`}
                    className="overflow-hidden mt-4 origin-top"
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <motion.div
                      className="px-4 py-3 text-blue-500 bg-gray-100 rounded-2xl"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex flex-wrap gap-2 items-center">
                        <p className="text-sm">정답</p>
                        {correctAnswerMeta.length > 0 ? (
                          correctAnswerMeta.map(({ id, label }) => (
                            <span
                              key={`correct-${question.id}-${id}`}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-blue-600 bg-white border border-blue-400 rounded-full"
                            >
                              <span>{id}</span>
                              <span>{label}</span>
                            </span>
                          ))
                        ) : (
                          <span className='text-sm text-gray-600'>정답 정보가 없습니다.</span>
                        )}
                      </div>
                      {question.reason && (
                        <p className="pt-1 text-sm">
                          {question.reason}
                        </p>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </section>
          )
        })}
              </>
            )}
            {initialPassCompleted && incorrectQuestions.length > 0 && (
              <section className='p-5'>
                <h2 className='mb-2 text-lg font-semibold'>틀린 문제 다시 풀기</h2>
                <p className='mb-4 text-sm text-gray-600'>
                  틀렸던 문제를 다시 선택해야만 다음 단계로 이동할 수 있습니다.
                </p>
                {incorrectQuestions.map((question) => {
                  const retrySelection = retrySelections[question.id];
                  const retryAnswered = hasSelections(retrySelection);
                  const isRetryCorrect = areSelectionsEqual(retrySelection, question.correctOptionIds);
                  const retryPendingSelection = !retryAnswered && question.allowMultiple ? (retryMultiSelections[question.id] || []) : [];
                  const retryActiveSelection = retryAnswered ? (retrySelection || []) : retryPendingSelection;
                  const retryCorrectMeta = getCorrectAnswerMeta(question);
                  const retryFeedback = retryFeedbacks[question.id];
                  return (
                    <section key={`retry-${question.id}`} className='relative p-4 mb-4 bg-gray-50 rounded-2xl border border-gray-100'>
                      <div className='relative'>
                        <h3 className='text-base font-semibold'>
                          {question.id}. {question.text}
                        </h3>
                        {retryAnswered && (
                          isRetryCorrect ? (
                            <AnswerCircle active={true} className='absolute -top-2 -left-4' size={40} />
                          ) : (
                            <AnswerCheck active={true} className='absolute -left-1 -top-3' />
                          )
                        )}
                      </div>
                      {retryFeedback && (
                        <p
                          className={`mt-2 text-sm ${
                            retryFeedback.type === 'error' ? 'text-red-500' : 'text-green-600'
                          }`}
                        >
                          {retryFeedback.message}
                        </p>
                      )}
                      <ul className='flex flex-wrap gap-y-4 gap-x-8 mt-4 mb-3'>
                        {question.options.map(opt => (
                          <li key={`retry-${question.id}-${opt.id}`}>
                            <button
                              className={`flex relative gap-2 items-center p-1 rounded-md ${retryAnswered ? 'opacity-60 cursor-not-allowed' : 'hover:bg-indigo-100'}`}
                              onClick={() => handleRetrySelect(question, opt.id)}
                              disabled={retryAnswered}
                            >
                              <div className='relative'>
                                <AnswerCircle
                                  active={retryActiveSelection.includes(opt.id)}
                                  className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
                                />
                                <span className='number_btn'>{opt.id}</span>
                              </div>
                              <span className='font-bold'>{opt.label}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                      {question.allowMultiple && !retryAnswered && (
                        <div className='flex flex-wrap gap-3 items-center mt-2 text-sm text-gray-600'>
                          <span>정답을 여러 개 선택할 수 있습니다. 선택 완료 버튼을 눌러주세요.</span>
                          <button
                            type="button"
                            className='px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-full disabled:opacity-40 disabled:cursor-not-allowed'
                            onClick={() => handleRetrySubmit(question)}
                            disabled={!hasSelections(retryPendingSelection)}
                          >
                            선택 완료
                          </button>
                          {retryPendingSelection.length > 0 && (
                            <span className='font-semibold text-indigo-600'>
                              현재 {retryPendingSelection.length}개 선택됨
                            </span>
                          )}
                        </div>
                      )}
                      {retryAnswered && question.reason && (
                        <motion.div
                          key={`retry-feedback-${question.id}`}
                          className='px-4 py-3 text-blue-500 bg-white rounded-2xl border border-blue-200 border-dashed'
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                        >
                          <div className='flex flex-wrap gap-2 items-center'>
                            <p className='text-sm'>정답</p>
                            {retryCorrectMeta.length > 0 ? (
                              retryCorrectMeta.map(({ id, label }) => (
                                <span
                                  key={`retry-correct-${question.id}-${id}`}
                                  className='flex items-center gap-1 px-2 py-1 text-xs font-semibold text-blue-600 bg-white border border-blue-400 rounded-full'
                                >
                                  <span>{id}</span>
                                  <span>{label}</span>
                                </span>
                              ))
                            ) : (
                              <span className='text-sm text-gray-600'>정답 정보가 없습니다.</span>
                            )}
                          </div>
                          <p className='pt-1 text-sm'>{question.reason}</p>
                        </motion.div>
                      )}
                    </section>
                  );
                })}
                {!retryComplete && (
                  <p className='text-sm text-red-500'>
                    재도전 문제를 모두 선택해야 합니다.
                  </p>
                )}
                {retryComplete && (
                  <p className='text-sm text-green-600'>
                    재도전 문제를 모두 선택했습니다.
                  </p>
                )}
              </section>
            )}

            {initialPassCompleted && retryComplete && incorrectQuestions.length === 0 && questions.length > 0 && (
              <section className='p-5 text-center'>
                <p className='font-semibold text-green-600'>
                  모든 동영상 퀴즈를 완료했습니다! 다음 단계로 이동하세요.
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </main>
  )
}